import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AnDateField from '../../../components/DateTime/DateField'
import { assignCourse, errorMessage, isOk } from '../../helpers/trainingApi'

// Assigns an e-learning course to one person, whatever their role needs.
const AssignCourseDialog = ({ open, onClose, course, employees = [], onAssigned }) => {
  const [employee, setEmployee] = useState(null)
  const [pathwayId, setPathwayId] = useState('')
  const [dueDate, setDueDate] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const pathways = course?.pathways ?? []
  const choosePathway = pathways.length > 1

  useEffect(() => {
    if (!open) return
    setEmployee(null)
    setPathwayId(pathways[0]?.id ?? '')
    setDueDate(null)
    setError(null)
  }, [open, course])

  const dueInvalid = !!dueDate && (!dueDate.isValid() || dueDate.isBefore(moment(), 'day'))

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!employee || dueInvalid) return
    setSaving(true)
    setError(null)
    const res = await assignCourse({
      employeeId: employee._id,
      courseId: course._id,
      ...(choosePathway ? { pathwayId } : {}),
      ...(dueDate ? { dueDate: moment(dueDate).endOf('day').toISOString() } : {}),
    })
    setSaving(false)
    if (isOk(res)) {
      onAssigned?.(employee)
    } else if (res.status === 409) {
      setError(
        `${employee.firstName} already has this course in progress. They can carry on from My Training.`,
      )
    } else {
      setError(errorMessage(res, 'Unable to assign this course. Please try again.'))
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Assign: {course?.title}</DialogTitle>
        <DialogContent>
          <Stack gap={2} paddingTop={1}>
            <Typography variant="body2" color="text.secondary">
              The course appears in their My Training straight away and they are sent a
              notification. Use this for someone whose role does not normally need the course.
            </Typography>
            <Autocomplete
              options={employees}
              value={employee}
              onChange={(event, value) => setEmployee(value)}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`.trim()}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Stack>
                    <Typography variant="body2">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.clinicalRole || 'No role set'}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Member of staff" required />}
            />
            {choosePathway && (
              <TextField
                select
                label="Pathway"
                value={pathwayId}
                onChange={(event) => setPathwayId(event.target.value)}
                required
                fullWidth
              >
                {pathways.map((pathway) => (
                  <MenuItem key={pathway.id} value={pathway.id}>
                    {pathway.title}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <AnDateField
              date={dueDate}
              onChange={(value) => setDueDate(value)}
              label="Due date (optional)"
              disablePast
              fullWidth
              helperText={dueInvalid ? 'Choose today or a later date' : 'Leave empty for no due date'}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="third" disabled={saving || !employee || dueInvalid}>
            {saving ? 'Assigning...' : 'Assign course'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default AssignCourseDialog
